
/**
 * ORION — Governance Domain
 *
 * File: governanceService.ts
 *
 * Responsibility:
 * - Frontend Service layer for Governance actions.
 * - Manages Voting Contexts (Open, Vote, Close).
 * - Retrieves recommendations.
 *
 * What this file does NOT do:
 * - Does not enforce rules (Backend does that).
 */
import axios from 'axios';
import { getToken } from '../../utils/auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const getClient = () => {
    const token = getToken();
    return axios.create({
        baseURL: API_URL,
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
};

export interface Decision {
    id: string;
    title: string;
    decision: string;
    rationale: string;
    status: string;
    created_at: string;
    links?: DecisionLink[];
}

export interface DecisionLink {
    object_id: string;
    link_type: string; // PRIMARY, SUPPORTING, etc
}

// ============================================================================
// VOTING INTERFACES (Command 08.B.3.B)
// ============================================================================

export interface VoteContext {
    id: string;
    object_id: string;
    context_type: 'CONTENT' | 'PROCESS';
    options: string[];
    status: 'OPEN' | 'CLOSED' | 'TIED' | 'EXPIRED';
    created_by: string;
    created_at: string;
    expires_at: string;
}

export interface Vote {
    id: string;
    context_id: string;
    voter_id: string;
    voter_type: 'HUMAN' | 'AGENT';
    choice: string;
    justification_preview: string;
    has_full_justification: boolean;
    weight_applied: number;
    created_at: string;
}

export interface VoteDistribution {
    total_votes: number;
    total_weight: number;
    by_choice: {
        [choice: string]: {
            votes: number;
            weight: number;
            voters: {
                voter_id: string;
                voter_type: string;
                justification_preview: string;
                has_full_justification: boolean;
            }[];
        };
    };
    is_tied: boolean;
    minority_votes: {
        choice: string;
        voter_id: string;
        voter_type: string;
        justification_preview: string;
        has_full_justification: boolean;
    }[];
}

export interface VoteRecommendation {
    recommended_choice: string;
    confidence: 'HIGH' | 'LOW';
    distribution: any;
    minority_votes: any[];
}

// ============================================================================
// GOVERNANCE SERVICE
// ============================================================================

export const governanceService = {
    // --- Decisions ---

    async createDecision(decision: {
        title: string;
        decision: string;
        rationale: string;
        tradeoffs: any;
        links: DecisionLink[];
    }) {
        const client = getClient();
        const res = await client.post('/governance/decisions', decision);
        return res.data;
    },

    async getDecisionsForObject(objectId: string) {
        const client = getClient();
        const res = await client.get<Decision[]>(`/governance/objects/${objectId}/decisions`);
        return res.data;
    },

    // --- Voting: Contexts ---

    async createVoteContext(
        objectId: string,
        contextType: 'CONTENT' | 'PROCESS',
        options: string[],
        expiresHours: number = 24
    ): Promise<VoteContext> {
        const client = getClient();
        const res = await client.post('/governance/votes/contexts', {
            object_id: objectId,
            context_type: contextType,
            options,
            expires_hours: expiresHours
        });
        return res.data;
    },

    async listVoteContexts(objectId: string): Promise<VoteContext[]> {
        const client = getClient();
        const res = await client.get(`/governance/votes/contexts?object_id=${objectId}`);
        return res.data;
    },

    async getVoteContext(contextId: string): Promise<VoteContext & { vote_count: number; distribution: VoteDistribution }> {
        const client = getClient();
        const res = await client.get(`/governance/votes/contexts/${contextId}`);
        return res.data;
    },

    async closeContext(contextId: string): Promise<VoteContext> {
        const client = getClient();
        const res = await client.post(`/governance/votes/contexts/${contextId}/close`);
        return res.data;
    },

    // --- Voting: Votes ---

    async castVote(
        contextId: string,
        choice: string,
        justification: string
    ): Promise<Vote> {
        const client = getClient();
        // CRITICAL: NO voter_type in request (backend derives it)
        const res = await client.post(`/governance/votes/contexts/${contextId}/votes`, {
            choice,
            justification
            // voter_type NOT sent (security requirement)
        });
        return res.data;
    },

    async listVotes(contextId: string): Promise<Vote[]> {
        const client = getClient();
        const res = await client.get(`/governance/votes/contexts/${contextId}/votes`);
        return res.data;
    },

    // --- Voting: Distribution ---

    async getDistribution(contextId: string): Promise<VoteDistribution> {
        const client = getClient();
        const res = await client.get(`/governance/votes/contexts/${contextId}/distribution`);
        return res.data;
    },

    // --- Voting: Recommendation ---

    async getRecommendation(objectId: string): Promise<VoteRecommendation | null> {
        const client = getClient();
        try {
            const res = await client.get(`/governance/votes/objects/${objectId}/recommendation`);
            return res.data;
        } catch (e: any) {
            if (e.response?.status === 204) {
                return null; // No recommendation available
            }
            throw e;
        }
    }
};
